import {
    calculatorFishingSourceTraces,
    calculatorInspectorUserLayerDocumentsEqual,
    parseCalculatorInspectorDataPresentationV1,
    parseCalculatorInspectorCustomCatalogItems,
    parseCalculatorInspectorModelV1,
    parseCalculatorInspectorResolvedProjectionV1,
    parseCalculatorInspectorUserLayerDocumentV4,
    selectedCalculatorTrace,
} from "./calculator-layer-inspector-contract.js";
import {
    buildRefuteEvidenceDraft,
    buildRevertUserOperationDraft,
    buildSetUserOperationMetadataDraft,
    buildSetItemGroupMembershipDraft,
    buildSetItemPresenceDraft,
    parseCalculatorLayerInspectorDraft,
} from "./calculator-layer-inspector-drafts.js";
import {
    buildCalculatorLootEditorAssignmentDrafts,
    buildCalculatorLootEditorGroupPresenceDraft,
    buildCalculatorLootEditorQuantityDraft,
    buildCalculatorLootEditorRateDrafts,
    buildCalculatorLootEditorRemovalDrafts,
    calculatorLootEditorFocusedControl,
    calculatorLootEditorQuantityFormHasValidInput,
    calculatorLootEditorRateFormHasValidInput,
    parseCalculatorLootEditorItemId,
    restoreCalculatorLootEditorFocusedControl,
    seedCalculatorLootEditorRateBasis,
} from "./calculator-loot-table-editor-interactions.js";
import {
    CALCULATOR_DATA_LANGUAGE_CHANGE_EVENT,
    calculatorDataLanguageTarget,
    calculatorDataText as t,
} from "./calculator-data-i18n.js";
import { bindCalculatorAssignmentConnections } from "./calculator-assignment-connections.js";
import {
    createCalculatorLayerInspectorRenderState,
    renderCalculatorLayerInspectorFromState,
    renderCalculatorLayerInspectorMarkup,
} from "./calculator-layer-inspector-view.js";
import {
    reconcileCalculatorLayerInspectorProjection,
} from "./calculator-layer-inspector-projection-reconciler.js";
import {
    bindProvenanceDetailBindings,
    createProvenanceDetailBindings,
} from "./provenance-detail-bindings.js";
import {
    DEFAULT_CALCULATOR_LOOT_SORT,
    parseCalculatorLootSort,
} from "./calculator-loot-sort.js";
import {
    parseCalculatorLootRateDisplayMode,
} from "./calculator-loot-rate-presentation.js";
import {
    calculatorLootSubjectsEqual,
    parseCalculatorLootSubject,
} from "./calculator-loot-subject.js";
import {
    lootConditionSwitcherActionFromTrigger,
} from "./loot-condition-switcher.js";
import {
    buildCalculatorCatalogItemPriceDraft,
    buildCalculatorCatalogItemQuantityDefaultDraft,
    calculatorCatalogItemPriceFormHasValidInput,
    calculatorCatalogItemQuantityDefaultFormHasValidInput,
} from "./calculator-catalog-item-facts-editor.js";

export const CALCULATOR_LAYER_INSPECTOR_TAG = "fishy-calculator-layer-inspector";
export const CALCULATOR_LAYER_DRAFT_EVENT = "fishystuff:calculator-layer-draft";
export const CALCULATOR_LAYER_DRAFT_FAILED_EVENT = "fishystuff:calculator-layer-draft-failed";
export const CALCULATOR_LAYER_DOCUMENT_ACTIVATED_EVENT =
    "fishystuff:calculator-user-layer-activated";
export const CALCULATOR_LAYER_CUSTOM_CATALOG_CHANGED_EVENT =
    "fishystuff:calculator-custom-catalog-changed";
export const CALCULATOR_MODEL_ITEMS_REQUEST_EVENT =
    "fishystuff:calculator-model-items-requested";
export const CALCULATOR_LAYER_ITEM_SELECTED_EVENT =
    "fishystuff:calculator-layer-item-selected";
export const CALCULATOR_LAYER_TRACE_SELECTED_EVENT =
    "fishystuff:calculator-layer-trace-selected";
export const CALCULATOR_LAYER_LOOT_SUBJECT_SELECTED_EVENT =
    "fishystuff:calculator-layer-loot-subject-selected";
export const CALCULATOR_RATE_ALTERNATIVE_SELECTED_EVENT =
    "fishystuff:calculator-rate-alternative-selected";

const HTMLElementBase = globalThis.HTMLElement ?? class {};

function upgradeProperty(element, property) {
    if (!Object.prototype.hasOwnProperty.call(element, property)) {
        return;
    }
    const value = element[property];
    delete element[property];
    element[property] = value;
}

function modelHasItemIdentity(model, itemId) {
    const operations = [
        ...(model?.baseFacts?.facts ?? []),
        ...(model?.sourceLayers ?? []).flatMap((layer) => layer.operations ?? []),
    ];
    return operations.some((operation) => (
        operation?.kind === "itemIdentity" && operation.itemId === itemId
    ));
}

function selectedItemTrace(projection, traceId, fishingSource, itemId) {
    const traces = calculatorFishingSourceTraces(projection, fishingSource);
    const requested = traces.find((trace) => trace.traceId === traceId);
    if (requested?.factKey?.itemId === itemId) {
        return requested;
    }
    return traces.find((trace) => (
        trace.factKey?.kind === "itemPresence"
        && trace.factKey.itemId === itemId
    )) ?? traces.find((trace) => trace.factKey?.itemId === itemId) ?? null;
}

function coherentSelectedTrace(projection, traceId, fishingSource, itemId) {
    return itemId === null
        ? selectedCalculatorTrace(projection, traceId, fishingSource)
        : selectedItemTrace(projection, traceId, fishingSource, itemId);
}

function customEvent(type, detail) {
    if (typeof globalThis.CustomEvent === "function") {
        return new globalThis.CustomEvent(type, {
            bubbles: true,
            composed: true,
            detail,
        });
    }
    const event = new Event(type, { bubbles: true });
    Object.defineProperty(event, "detail", { value: detail });
    return event;
}

function visibleError(error) {
    return error instanceof Error ? error.message : String(error);
}

function serializedError(error) {
    return Object.freeze({
        name: error instanceof Error ? error.name : "Error",
        message: visibleError(error),
    });
}

function translatedMessage(message) {
    return message && typeof message === "object"
        ? t(message.key, message.fallback, message.variables)
        : String(message ?? "");
}

export class FishyCalculatorLayerInspector extends HTMLElementBase {
    constructor() {
        super();
        this._model = null;
        this._projection = null;
        this._userLayerDocument = null;
        this._userLayerDocumentSource = null;
        this._customCatalogItems = Object.freeze([]);
        this._customCatalogItemsSource = null;
        this._catalogSearchUrl = "";
        this._dataPresentation = null;
        this._lootSort = DEFAULT_CALCULATOR_LOOT_SORT;
        this._lootRateDisplayMode = "normalized";
        this._rankingEvidenceQuery = "";
        this._selectedItemId = null;
        this._itemFactsEditorItemId = null;
        this._selectedLootSubject = null;
        this._modelLoading = false;
        this._rateEditor = null;
        this._selectedTraceId = null;
        this._draftPreparer = null;
        this._draftPending = false;
        this._draftRequestRevision = 0;
        this._acceptAsyncDrafts = false;
        this._errorMessage = "";
        this._announcement = "";
        this._languageTarget = null;
        this._renderFrameId = 0;
        this._renderedProjection = null;
        this._renderedUserLayerDocument = null;
        this._renderedViewState = null;
        this._renderedErrorMessage = "";
        this._handleClickBound = (event) => this._handleClick(event);
        this._handleInputBound = (event) => this._handleInput(event);
        this._handleChangeBound = (event) => this._handleChange(event);
        this._handleLanguageChangeBound = () => this.render();
    }

    connectedCallback() {
        this._acceptAsyncDrafts = true;
        upgradeProperty(this, "model");
        upgradeProperty(this, "projection");
        upgradeProperty(this, "userLayerDocument");
        upgradeProperty(this, "customCatalogItems");
        upgradeProperty(this, "catalogSearchUrl");
        upgradeProperty(this, "dataPresentation");
        upgradeProperty(this, "lootSort");
        upgradeProperty(this, "lootRateDisplayMode");
        upgradeProperty(this, "rankingEvidenceQuery");
        upgradeProperty(this, "selectedItemId");
        upgradeProperty(this, "selectedLootSubject");
        upgradeProperty(this, "modelLoading");
        upgradeProperty(this, "selectedTraceId");
        upgradeProperty(this, "draftPreparer");
        this.addEventListener("click", this._handleClickBound);
        this.addEventListener("input", this._handleInputBound);
        this.addEventListener("change", this._handleChangeBound);
        this._languageTarget = calculatorDataLanguageTarget();
        this._languageTarget?.addEventListener?.(
            CALCULATOR_DATA_LANGUAGE_CHANGE_EVENT,
            this._handleLanguageChangeBound,
        );
        this.render();
    }

    disconnectedCallback() {
        this._acceptAsyncDrafts = false;
        this._draftRequestRevision += 1;
        this._draftPending = false;
        this.removeAttribute?.("aria-busy");
        if (
            this._renderFrameId
            && typeof globalThis.cancelAnimationFrame === "function"
        ) {
            globalThis.cancelAnimationFrame(this._renderFrameId);
        }
        this._renderFrameId = 0;
        this.removeEventListener("click", this._handleClickBound);
        this.removeEventListener("input", this._handleInputBound);
        this.removeEventListener("change", this._handleChangeBound);
        this._languageTarget?.removeEventListener?.(
            CALCULATOR_DATA_LANGUAGE_CHANGE_EVENT,
            this._handleLanguageChangeBound,
        );
        this._languageTarget = null;
    }

    get model() {
        return this._model;
    }

    set model(value) {
        const model = value === null ? null : parseCalculatorInspectorModelV1(value);
        const revisionChanged = model?.modelRevision !== this._model?.modelRevision;
        if (model && this._projection) {
            parseCalculatorInspectorResolvedProjectionV1(this._projection, model);
        }
        if (this._dataPresentation) {
            parseCalculatorInspectorDataPresentationV1(this._dataPresentation, model);
        }
        this._model = model;
        if (revisionChanged) {
            this._selectedItemId = null;
            this._selectedLootSubject = null;
            this._selectedTraceId = null;
            this._rateEditor = null;
        }
        this.render();
    }

    get projection() {
        return this._projection;
    }

    set projection(value) {
        this._projection = value === null
            ? null
            : parseCalculatorInspectorResolvedProjectionV1(value, this._model);
        const selected = coherentSelectedTrace(
            this._projection,
            this._selectedTraceId,
            this._model?.selection?.fishingSource ?? null,
            this._selectedItemId,
        );
        this._selectedTraceId = selected?.traceId ?? null;
        this.render();
    }

    get userLayerDocument() {
        return this._userLayerDocument;
    }

    set userLayerDocument(value) {
        const parsed = value === null
            ? null
            : parseCalculatorInspectorUserLayerDocumentV4(value);
        if (calculatorInspectorUserLayerDocumentsEqual(
            parsed,
            this._userLayerDocument,
        )) {
            this._userLayerDocumentSource = value;
            return;
        }
        this._userLayerDocumentSource = value;
        this._userLayerDocument = parsed;
        this.render();
    }

    get customCatalogItems() {
        return this._customCatalogItems;
    }

    set customCatalogItems(value) {
        this._customCatalogItemsSource = value;
        this._customCatalogItems = Object.freeze(
            parseCalculatorInspectorCustomCatalogItems(value),
        );
        this.render();
    }

    get catalogSearchUrl() {
        return this._catalogSearchUrl;
    }

    set catalogSearchUrl(value) {
        if (typeof value !== "string") {
            throw new TypeError("catalogSearchUrl must be a string");
        }
        if (value === this._catalogSearchUrl) {
            return;
        }
        this._catalogSearchUrl = value;
        this.render();
    }

    get dataPresentation() {
        return this._dataPresentation;
    }

    get lootSort() {
        return this._lootSort;
    }

    set lootSort(value) {
        const parsed = parseCalculatorLootSort(value);
        if (parsed.by === this._lootSort.by && parsed.order === this._lootSort.order) {
            return;
        }
        this._lootSort = parsed;
        this.render();
    }

    get lootRateDisplayMode() {
        return this._lootRateDisplayMode;
    }

    set lootRateDisplayMode(value) {
        const mode = parseCalculatorLootRateDisplayMode(
            value,
            "lootRateDisplayMode",
        );
        if (mode === this._lootRateDisplayMode) {
            return;
        }
        this._lootRateDisplayMode = mode;
        this.render();
    }

    get rankingEvidenceQuery() {
        return this._rankingEvidenceQuery;
    }

    set rankingEvidenceQuery(value) {
        if (typeof value !== "string") {
            throw new TypeError("rankingEvidenceQuery must be a string");
        }
        if (value === this._rankingEvidenceQuery) {
            return;
        }
        this._rankingEvidenceQuery = value;
        this.render();
    }

    set dataPresentation(value) {
        const parsed = parseCalculatorInspectorDataPresentationV1(value, this._model);
        if (parsed === this._dataPresentation) {
            return;
        }
        this._dataPresentation = parsed;
        this.render();
    }

    get selectedTraceId() {
        return this._selectedTraceId;
    }

    get selectedItemId() {
        return this._selectedItemId;
    }

    get selectedLootSubject() {
        return this._selectedLootSubject;
    }

    get modelLoading() {
        return this._modelLoading;
    }

    set modelLoading(value) {
        if (typeof value !== "boolean") {
            throw new TypeError("modelLoading must be a boolean");
        }
        if (value === this._modelLoading) {
            return;
        }
        this._modelLoading = value;
        this.render();
    }

    set selectedItemId(value) {
        const parsed = value === null ? null : parseCalculatorLootEditorItemId(value);
        const itemChanged = parsed !== this._selectedItemId;
        const selectedTraceId = coherentSelectedTrace(
            this._projection,
            this._selectedTraceId,
            this._model?.selection?.fishingSource ?? null,
            parsed,
        )?.traceId ?? null;
        if (
            parsed === this._selectedItemId
            && selectedTraceId === this._selectedTraceId
        ) {
            return;
        }
        this._selectedItemId = parsed;
        this._selectedTraceId = selectedTraceId;
        if (itemChanged) {
            this._rateEditor = null;
        }
        this.render();
    }

    set selectedLootSubject(value) {
        const parsed = parseCalculatorLootSubject(
            value,
            "selectedLootSubject",
        );
        if (calculatorLootSubjectsEqual(parsed, this._selectedLootSubject)) {
            return;
        }
        this._selectedLootSubject = parsed;
        this._rateEditor = null;
        this.render();
    }

    set selectedTraceId(value) {
        if (value !== null && (typeof value !== "string" || !value.trim())) {
            throw new TypeError("selectedTraceId must be a non-empty string or null");
        }
        const selected = coherentSelectedTrace(
            this._projection,
            value,
            this._model?.selection?.fishingSource ?? null,
            this._selectedItemId,
        );
        const selectedTraceId = selected?.traceId
            ?? (this._selectedItemId === null ? value : null);
        if (selectedTraceId !== this._selectedTraceId) {
            this._rateEditor = null;
        }
        this._selectedTraceId = selectedTraceId;
        this.render();
    }

    get draftPreparer() {
        return this._draftPreparer;
    }

    set draftPreparer(value) {
        if (value !== null && typeof value !== "function") {
            throw new TypeError("draftPreparer must be a function or null");
        }
        this._draftPreparer = value;
    }

    /**
     * Applies the complete host-owned state as one validated render boundary.
     *
     * The host is the sole props owner for this method. Individual property
     * setters remain available for focused component interactions, while host
     * synchronization must never rebuild this large editor once per property.
     */
    setHostState(value) {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            throw new TypeError("calculator inspector host state must be an object");
        }
        const model = value.model === this._model
            ? this._model
            : value.model === null
                ? null
                : parseCalculatorInspectorModelV1(value.model);
        const projection = value.projection === this._projection
            ? this._projection
            : value.projection === null
                ? null
                : parseCalculatorInspectorResolvedProjectionV1(
                    value.projection,
                    model,
                );
        const parsedUserLayerDocument =
            value.userLayerDocument === this._userLayerDocumentSource
                ? this._userLayerDocument
                : value.userLayerDocument === null
                    ? null
                    : parseCalculatorInspectorUserLayerDocumentV4(
                        value.userLayerDocument,
                    );
        const userLayerDocument = calculatorInspectorUserLayerDocumentsEqual(
            parsedUserLayerDocument,
            this._userLayerDocument,
        )
            ? this._userLayerDocument
            : parsedUserLayerDocument;
        const dataPresentation = value.dataPresentation === this._dataPresentation
            ? this._dataPresentation
            : parseCalculatorInspectorDataPresentationV1(
                value.dataPresentation,
                model,
            );
        const customCatalogItems =
            value.customCatalogItems === this._customCatalogItemsSource
            ? this._customCatalogItems
            : Object.freeze(
                parseCalculatorInspectorCustomCatalogItems(
                    value.customCatalogItems,
                ),
            );
        const lootSort = parseCalculatorLootSort(value.lootSort);
        const lootRateDisplayMode = parseCalculatorLootRateDisplayMode(
            value.lootRateDisplayMode,
            "lootRateDisplayMode",
        );
        if (typeof value.catalogSearchUrl !== "string") {
            throw new TypeError("catalogSearchUrl must be a string");
        }
        if (typeof value.rankingEvidenceQuery !== "string") {
            throw new TypeError("rankingEvidenceQuery must be a string");
        }
        const selectedItemId = value.selectedItemId === null
            ? null
            : parseCalculatorLootEditorItemId(value.selectedItemId);
        const selectedLootSubject = parseCalculatorLootSubject(
            value.selectedLootSubject ?? null,
            "selectedLootSubject",
        );
        const lootSubjectChanged = !calculatorLootSubjectsEqual(
            selectedLootSubject,
            this._selectedLootSubject,
        );
        if (typeof value.modelLoading !== "boolean") {
            throw new TypeError("modelLoading must be a boolean");
        }
        if (
            value.draftPreparer !== null
            && typeof value.draftPreparer !== "function"
        ) {
            throw new TypeError("draftPreparer must be a function or null");
        }
        const selectedTraceId = coherentSelectedTrace(
            projection,
            value.selectedTraceId,
            model?.selection?.fishingSource ?? null,
            selectedItemId,
        )?.traceId ?? null;
        const revisionChanged = model?.modelRevision !== this._model?.modelRevision;
        const regionalInputsUnchanged = !revisionChanged
            && model === this._model
            && customCatalogItems === this._customCatalogItems
            && dataPresentation === this._dataPresentation
            && lootSort.by === this._lootSort.by
            && lootSort.order === this._lootSort.order
            && lootRateDisplayMode === this._lootRateDisplayMode
            && value.catalogSearchUrl === this._catalogSearchUrl
            && value.rankingEvidenceQuery === this._rankingEvidenceQuery
            && selectedItemId === this._selectedItemId
            && calculatorLootSubjectsEqual(
                selectedLootSubject,
                this._selectedLootSubject,
            )
            && value.modelLoading === this._modelLoading
            && value.draftPreparer === this._draftPreparer
            && selectedTraceId === this._selectedTraceId
            && translatedMessage(this._errorMessage) === this._renderedErrorMessage
            && Boolean(this._renderedViewState);
        if (
            !revisionChanged
            && projection === this._projection
            && userLayerDocument === this._userLayerDocument
            && customCatalogItems === this._customCatalogItems
            && dataPresentation === this._dataPresentation
            && lootSort.by === this._lootSort.by
            && lootSort.order === this._lootSort.order
            && lootRateDisplayMode === this._lootRateDisplayMode
            && value.catalogSearchUrl === this._catalogSearchUrl
            && value.rankingEvidenceQuery === this._rankingEvidenceQuery
            && selectedItemId === this._selectedItemId
            && calculatorLootSubjectsEqual(
                selectedLootSubject,
                this._selectedLootSubject,
            )
            && value.modelLoading === this._modelLoading
            && value.draftPreparer === this._draftPreparer
            && selectedTraceId === this._selectedTraceId
        ) {
            this._userLayerDocumentSource = value.userLayerDocument;
            this._customCatalogItemsSource = value.customCatalogItems;
            return false;
        }
        this._model = model;
        this._projection = projection;
        this._userLayerDocument = userLayerDocument;
        this._userLayerDocumentSource = value.userLayerDocument;
        this._customCatalogItems = customCatalogItems;
        this._customCatalogItemsSource = value.customCatalogItems;
        this._catalogSearchUrl = value.catalogSearchUrl;
        this._dataPresentation = dataPresentation;
        this._lootSort = lootSort;
        this._lootRateDisplayMode = lootRateDisplayMode;
        this._rankingEvidenceQuery = value.rankingEvidenceQuery;
        this._selectedItemId = selectedItemId;
        this._selectedLootSubject = selectedLootSubject;
        this._modelLoading = value.modelLoading;
        this._draftPreparer = value.draftPreparer;
        this._selectedTraceId = selectedTraceId;
        if (revisionChanged || lootSubjectChanged) {
            this._rateEditor = null;
        }
        if (
            regionalInputsUnchanged
            && projection
            && userLayerDocument
            && projection !== this._renderedProjection
        ) {
            const nextViewState = createCalculatorLayerInspectorRenderState(
                this._renderInput(),
            );
            let reconciled = false;
            try {
                reconciled = reconcileCalculatorLayerInspectorProjection({
                    host: this,
                    previousState: this._renderedViewState,
                    nextState: nextViewState,
                    previousDocument: this._renderedUserLayerDocument,
                    nextDocument: userLayerDocument,
                    lootSort: this._lootSort,
                    announcement: translatedMessage(this._announcement),
                });
            } catch {
                this.render();
                return true;
            }
            if (reconciled) {
                this._cancelScheduledRender();
                this._commitRenderedState(nextViewState);
                return true;
            }
        }
        this.render();
        return true;
    }

    _renderInput() {
        return {
            model: this._model,
            projection: this._projection,
            userLayerDocument: this._userLayerDocument,
            selectedTraceId: this._selectedTraceId,
            customCatalogItems: this._customCatalogItems,
            catalogSearchUrl: this._catalogSearchUrl,
            dataPresentation: this._dataPresentation?.sankey ?? null,
            lootSort: this._lootSort,
            lootRateDisplayMode: this._lootRateDisplayMode,
            rankingEvidenceQuery: this._rankingEvidenceQuery,
            selectedItemId: this._selectedItemId,
            itemFactsEditorItemId: this._itemFactsEditorItemId,
            selectedLootSubject: this._selectedLootSubject,
            modelLoading: this._modelLoading,
            rateEditor: this._rateEditor,
            errorMessage: translatedMessage(this._errorMessage),
            announcement: translatedMessage(this._announcement),
        };
    }

    _cancelScheduledRender() {
        if (
            this._renderFrameId
            && typeof globalThis.cancelAnimationFrame === "function"
        ) {
            globalThis.cancelAnimationFrame(this._renderFrameId);
        }
        this._renderFrameId = 0;
    }

    _commitRenderedState(viewState) {
        this._renderedProjection = this._projection;
        this._renderedUserLayerDocument = this._userLayerDocument;
        this._renderedViewState = viewState;
        this._renderedErrorMessage = translatedMessage(this._errorMessage);
    }

    scheduleRender() {
        if (this._renderFrameId) {
            return;
        }
        if (typeof globalThis.requestAnimationFrame !== "function") {
            this.render();
            return;
        }
        this._renderFrameId = globalThis.requestAnimationFrame(() => {
            this._renderFrameId = 0;
            this.render();
        });
    }

    render() {
        if (!("innerHTML" in this)) {
            return;
        }
        this._cancelScheduledRender();
        const focused = calculatorLootEditorFocusedControl(this);
        const provenanceDetailBindings = createProvenanceDetailBindings();
        const input = this._renderInput();
        const viewState = this._model && this._projection && this._userLayerDocument
            ? createCalculatorLayerInspectorRenderState(input)
            : null;
        this.innerHTML = viewState
            ? renderCalculatorLayerInspectorFromState(viewState, {
                provenanceDetailBindings,
            })
            : renderCalculatorLayerInspectorMarkup({
                ...input,
                provenanceDetailBindings,
            });
        bindProvenanceDetailBindings(this, provenanceDetailBindings);
        bindCalculatorAssignmentConnections(this);
        restoreCalculatorLootEditorFocusedControl(this, focused);
        this._commitRenderedState(viewState);
    }

    requestDraft(draft) {
        return this.requestDraftBatch([draft]);
    }

    requestDraftBatch(drafts) {
        if (this._modelLoading) {
            this._errorMessage = {
                key: "calculator.data.error.wait_for_model",
                fallback: "Wait for the selected model facts to finish loading before editing the user layer.",
                variables: {},
            };
            this._announcement = this._errorMessage;
            this.render();
            return null;
        }
        if (!Array.isArray(drafts) || drafts.length === 0) {
            throw new TypeError("requestDraftBatch requires at least one draft");
        }
        if (this._draftPending) {
            this._errorMessage = {
                key: "calculator.data.error.wait_for_author",
                fallback: "Wait for the current authored edit to finish before editing again.",
                variables: {},
            };
            this.render();
            return null;
        }
        const normalizedDrafts = Object.freeze(
            drafts.map((draft) => parseCalculatorLayerInspectorDraft(draft)),
        );
        for (const normalizedDraft of normalizedDrafts) {
            this.dispatchEvent(customEvent(CALCULATOR_LAYER_DRAFT_EVENT, Object.freeze({
                modelRevision: this._model?.modelRevision ?? null,
                draft: normalizedDraft,
            })));
        }
        let activeDraft = normalizedDrafts[0];
        try {
            if (!this._userLayerDocument) {
                throw new Error("A finalized v4 user-layer document is required before editing");
            }
            if (!this._draftPreparer) {
                throw new Error("The calculator engine draft preparer is unavailable");
            }
            let normalized = this._userLayerDocument;
            for (const [index, normalizedDraft] of normalizedDrafts.entries()) {
                activeDraft = normalizedDraft;
                const finalizedDocument = this._draftPreparer(
                    normalized,
                    normalizedDraft,
                );
                if (finalizedDocument && typeof finalizedDocument.then === "function") {
                    return this._completeAsyncDraftBatch({
                        firstResult: finalizedDocument,
                        firstIndex: index,
                        normalizedDrafts,
                        startingDocument: this._userLayerDocument,
                        startingModelRevision: this._model?.modelRevision ?? null,
                    });
                }
                normalized = parseCalculatorInspectorUserLayerDocumentV4(finalizedDocument);
            }
            return this._activatePreparedDraftBatch(normalizedDrafts, normalized);
        } catch (error) {
            return this._failDraft(activeDraft, error);
        }
    }

    _activatePreparedDraftBatch(normalizedDrafts, normalized) {
        if (calculatorInspectorUserLayerDocumentsEqual(
            normalized,
            this._userLayerDocument,
        )) {
            this._errorMessage = "";
            return this._userLayerDocument;
        }
        this._userLayerDocument = normalized;
        this._errorMessage = "";
        this._announcement = {
            key: "calculator.data.announcement.user_layer_updated",
            fallback: "User layer updated.",
            variables: {},
        };
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_DOCUMENT_ACTIVATED_EVENT,
            Object.freeze({
                draft: normalizedDrafts.at(-1),
                drafts: normalizedDrafts,
                document: normalized,
            }),
        ));
        this.scheduleRender();
        return normalized;
    }

    _failDraft(draft, error) {
        this._errorMessage = visibleError(error);
        this.render();
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_DRAFT_FAILED_EVENT,
            Object.freeze({ draft, error: serializedError(error) }),
        ));
        return null;
    }

    _completeAsyncDraftBatch({
        firstResult,
        firstIndex,
        normalizedDrafts,
        startingDocument,
        startingModelRevision,
    }) {
        const requestRevision = this._draftRequestRevision + 1;
        this._draftRequestRevision = requestRevision;
        this._draftPending = true;
        this.setAttribute?.("aria-busy", "true");

        return (async () => {
            let activeDraft = normalizedDrafts[firstIndex];
            const requestIsCurrent = () => (
                this._acceptAsyncDrafts
                && requestRevision === this._draftRequestRevision
                && this._userLayerDocument === startingDocument
                && (this._model?.modelRevision ?? null) === startingModelRevision
            );
            try {
                let normalized = parseCalculatorInspectorUserLayerDocumentV4(
                    await firstResult,
                );
                for (let index = firstIndex + 1; index < normalizedDrafts.length; index += 1) {
                    activeDraft = normalizedDrafts[index];
                    normalized = parseCalculatorInspectorUserLayerDocumentV4(
                        await this._draftPreparer(normalized, activeDraft),
                    );
                }
                if (!requestIsCurrent()) return null;
                return this._activatePreparedDraftBatch(normalizedDrafts, normalized);
            } catch (error) {
                if (!requestIsCurrent()) return null;
                return this._failDraft(activeDraft, error);
            } finally {
                if (requestRevision === this._draftRequestRevision) {
                    this._draftPending = false;
                    this.removeAttribute?.("aria-busy");
                }
            }
        })();
    }

    _value(selector) {
        return String(this.querySelector(selector)?.value ?? "").trim();
    }

    _number(selector) {
        const raw = this._value(selector);
        return raw === "" ? Number.NaN : Number(raw);
    }

    _elementWithAttribute(selector, attribute, expected, root = this) {
        return Array.from(root.querySelectorAll?.(selector) ?? [])
            .find((element) => element.getAttribute(attribute) === expected)
            ?? null;
    }

    _requestItemModel(itemId) {
        const parsedItemId = parseCalculatorLootEditorItemId(itemId);
        const alreadyRequested = this._model?.selection?.supplementalItemIds
            ?.includes(parsedItemId) ?? false;
        if (modelHasItemIdentity(this._model, parsedItemId) || alreadyRequested) {
            return false;
        }
        this._announcement = {
            key: "calculator.data.announcement.loading_item",
            fallback: "Loading facts for Item { $id }.",
            variables: { id: parsedItemId },
        };
        this.dispatchEvent(customEvent(
            CALCULATOR_MODEL_ITEMS_REQUEST_EVENT,
            Object.freeze({
                modelRevision: this._model?.modelRevision ?? null,
                itemIds: Object.freeze(this._customItemIds([parsedItemId])),
                customItemIds: Object.freeze(this._customItemIds()),
            }),
        ));
        return true;
    }

    _customItemIds(additionalItemIds = []) {
        return [...new Set([
            ...this._customCatalogItems.map((item) => item.itemId),
            ...additionalItemIds.map((itemId) => (
                parseCalculatorLootEditorItemId(itemId)
            )),
        ])].sort((left, right) => left - right);
    }

    _publishCustomCatalogItems() {
        const items = Object.freeze([...this._customCatalogItems]);
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_CUSTOM_CATALOG_CHANGED_EVENT,
            Object.freeze({ items }),
        ));
        this.dispatchEvent(customEvent(
            CALCULATOR_MODEL_ITEMS_REQUEST_EVENT,
            Object.freeze({
                modelRevision: this._model?.modelRevision ?? null,
                itemIds: Object.freeze(this._customItemIds()),
                customItemIds: Object.freeze(this._customItemIds()),
            }),
        ));
    }

    _addCustomCatalogItem(itemId, label) {
        const parsedItemId = parseCalculatorLootEditorItemId(itemId);
        const parsedLabel = String(label ?? "").trim();
        const existing = this._customCatalogItems.find((item) => (
            item.itemId === parsedItemId
        ));
        if (!existing) {
            this._customCatalogItems = Object.freeze(
                parseCalculatorInspectorCustomCatalogItems([
                    ...this._customCatalogItems,
                    { itemId: parsedItemId, label: parsedLabel },
                ]),
            );
            this._customCatalogItemsSource = this._customCatalogItems;
            this._announcement = {
                key: "calculator.data.announcement.custom_item_added",
                fallback: "{ $item } added to Custom.",
                variables: { item: parsedLabel },
            };
            this._publishCustomCatalogItems();
        }
        this._selectItem(parsedItemId);
        this.render();
        return parsedItemId;
    }

    _removeCustomCatalogItem(itemId) {
        const parsedItemId = parseCalculatorLootEditorItemId(itemId);
        const removed = this._customCatalogItems.find((item) => (
            item.itemId === parsedItemId
        ));
        if (!removed) {
            return false;
        }
        this._customCatalogItems = Object.freeze(
            this._customCatalogItems.filter((item) => item.itemId !== parsedItemId),
        );
        this._customCatalogItemsSource = this._customCatalogItems;
        if (this._selectedItemId === parsedItemId) {
            this._selectedItemId = null;
            this._selectedTraceId = null;
            this._selectedLootSubject = null;
            this._rateEditor = null;
            this.dispatchEvent(customEvent(
                CALCULATOR_LAYER_ITEM_SELECTED_EVENT,
                Object.freeze({
                    modelRevision: this._model?.modelRevision ?? null,
                    itemId: null,
                }),
            ));
        }
        if (this._itemFactsEditorItemId === parsedItemId) {
            this._itemFactsEditorItemId = null;
        }
        this._announcement = {
            key: "calculator.data.announcement.custom_item_removed",
            fallback: "{ $item } removed from Custom.",
            variables: { item: removed.label },
        };
        this._publishCustomCatalogItems();
        this.render();
        return true;
    }

    _selectLootSubject(value) {
        const subject = parseCalculatorLootSubject(
            value,
            "selectedLootSubject",
        );
        if (calculatorLootSubjectsEqual(subject, this._selectedLootSubject)) {
            return subject;
        }
        this._selectedLootSubject = subject;
        this._rateEditor = null;
        this._errorMessage = "";
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_LOOT_SUBJECT_SELECTED_EVENT,
            Object.freeze({
                modelRevision: this._model?.modelRevision ?? null,
                subject,
            }),
        ));
        return subject;
    }

    _selectLootGroup(groupId) {
        return this._selectLootSubject({ kind: "group", groupId });
    }

    _selectLootRow(itemId, groupId, requestedTraceId = this._selectedTraceId) {
        const parsedItemId = this._selectItem(itemId, requestedTraceId);
        return this._selectLootSubject({
            kind: "row",
            groupId,
            itemId: parsedItemId,
        });
    }

    _selectItem(itemId, requestedTraceId = this._selectedTraceId) {
        const parsedItemId = parseCalculatorLootEditorItemId(itemId);
        this._selectedItemId = parsedItemId;
        this._selectedTraceId = selectedItemTrace(
            this._projection,
            requestedTraceId,
            this._model?.selection?.fishingSource ?? null,
            parsedItemId,
        )?.traceId ?? null;
        this._rateEditor = null;
        this._errorMessage = "";
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_ITEM_SELECTED_EVENT,
            Object.freeze({
                modelRevision: this._model?.modelRevision ?? null,
                itemId: parsedItemId,
            }),
        ));
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_TRACE_SELECTED_EVENT,
            Object.freeze({ traceId: this._selectedTraceId }),
        ));
        return parsedItemId;
    }

    _assignItemToGroup(itemId, groupId) {
        this._selectedItemId = parseCalculatorLootEditorItemId(itemId);
        if (this._requestItemModel(this._selectedItemId)) {
            this._announcement = {
                key: "calculator.data.announcement.loading_before_add",
                fallback: "Loading this item's facts. Add it to a group after loading finishes.",
                variables: {},
            };
            this.render();
            return null;
        }
        const drafts = buildCalculatorLootEditorAssignmentDrafts({
            model: this._model,
            projection: this._projection,
            itemId,
            groupId,
        });
        if (!drafts.length) {
            this._errorMessage = "";
            this.render();
            return this._userLayerDocument;
        }
        return this.requestDraftBatch(drafts);
    }

    _removeItemFromGroup(itemId, groupId) {
        return this.requestDraftBatch(buildCalculatorLootEditorRemovalDrafts({
            model: this._model,
            projection: this._projection,
            itemId,
            groupId,
        }));
    }

    _setGroupPresence(groupId, presence) {
        return this.requestDraft(buildCalculatorLootEditorGroupPresenceDraft({
            model: this._model,
            projection: this._projection,
            groupId,
            presence,
        }));
    }

    _editIdentity() {
        return {
            fishingSource: this._model?.selection?.fishingSource,
            itemId: this._number("[data-edit-item-id]"),
            groupId: this._value("[data-edit-current-group]"),
        };
    }

    _reportActionError(error) {
        this._errorMessage = visibleError(error);
        this.render();
        this.dispatchEvent(customEvent(
            CALCULATOR_LAYER_DRAFT_FAILED_EVENT,
            Object.freeze({ draft: null, error: serializedError(error) }),
        ));
    }

    _activateLiveLootDrafts(buildDrafts) {
        try {
            const drafts = buildDrafts();
            const focused = calculatorLootEditorFocusedControl(this);
            const result = this.requestDraftBatch(drafts);
            restoreCalculatorLootEditorFocusedControl(this, focused);
            return result;
        } catch (error) {
            this._reportActionError(error);
            return null;
        }
    }

    _activateLiveLootRate(form) {
        if (!calculatorLootEditorRateFormHasValidInput(form)) {
            return null;
        }
        return this._activateLiveLootDrafts(() => (
            buildCalculatorLootEditorRateDrafts({
                model: this._model,
                projection: this._projection,
                form,
            })
        ));
    }

    _activateLiveLootQuantity(form) {
        if (!calculatorLootEditorQuantityFormHasValidInput(form)) {
            return null;
        }
        return this._activateLiveLootDrafts(() => [
            buildCalculatorLootEditorQuantityDraft({
                model: this._model,
                form,
            }),
        ]);
    }

    _activateLiveCatalogItemPrice(form) {
        if (!calculatorCatalogItemPriceFormHasValidInput(form)) {
            return null;
        }
        return this._activateLiveLootDrafts(() => [
            buildCalculatorCatalogItemPriceDraft(form),
        ]);
    }

    _activateLiveCatalogItemQuantityDefault(form) {
        if (!calculatorCatalogItemQuantityDefaultFormHasValidInput(form)) {
            return null;
        }
        return this._activateLiveLootDrafts(() => [
            buildCalculatorCatalogItemQuantityDefaultDraft(form),
        ]);
    }

    _applyLiveOperationMetadata(operationId) {
        const input = this._elementWithAttribute(
            "[data-operation-comment-for]",
            "data-operation-comment-for",
            operationId,
        );
        const confidenceInput = this._elementWithAttribute(
            "[data-operation-confidence-for]",
            "data-operation-confidence-for",
            operationId,
        );
        const confidence = String(confidenceInput?.value ?? "");
        if (confidence !== "known" && confidence !== "guessed") {
            return null;
        }
        try {
            return this.requestDraft(buildSetUserOperationMetadataDraft(
                operationId,
                String(input?.value ?? "").trim() || null,
                confidence,
            ));
        } catch (error) {
            this._reportActionError(error);
            return null;
        }
    }

    _handleInput(event) {
        if (event.target?.matches?.("[data-loot-editor-rate-value]")) {
            this._activateLiveLootRate(
                event.target.closest?.("[data-loot-editor-rate-form]"),
            );
            return;
        }
        if (
            event.target?.matches?.("[data-loot-editor-quantity-minimum]")
            || event.target?.matches?.("[data-loot-editor-quantity-maximum]")
        ) {
            this._activateLiveLootQuantity(
                event.target.closest?.("[data-loot-editor-quantity-form]"),
            );
            return;
        }
        if (event.target?.matches?.("[data-catalog-item-price]")) {
            this._activateLiveCatalogItemPrice(
                event.target.closest?.("[data-catalog-item-facts-form]"),
            );
            return;
        }
        if (
            event.target?.matches?.("[data-catalog-item-quantity-minimum]")
            || event.target?.matches?.("[data-catalog-item-quantity-maximum]")
        ) {
            this._activateLiveCatalogItemQuantityDefault(
                event.target.closest?.("[data-catalog-item-facts-form]"),
            );
            return;
        }
        if (event.target?.matches?.("[data-operation-comment-for]")) {
            this._applyLiveOperationMetadata(
                event.target.getAttribute?.("data-operation-comment-for") ?? "",
            );
            return;
        }
        if (event.target?.matches?.("[data-ranking-evidence-search]")) {
            this._rankingEvidenceQuery = String(event.target.value ?? "");
        }
    }

    _handleChange(event) {
        if (event.target?.matches?.("fishy-searchable-dropdown[data-loot-editor-catalog-picker]")) {
            this._addCustomCatalogItem(event.target.value, event.target.label);
            return;
        }
        if (event.target?.matches?.("[data-loot-editor-rate-basis]")) {
            const form = event.target.closest?.("[data-loot-editor-rate-form]");
            try {
                const inputBasis = seedCalculatorLootEditorRateBasis(
                    form,
                    event.target,
                );
                if (this._rateEditor) {
                    this._rateEditor = Object.freeze({
                        ...this._rateEditor,
                        inputBasis,
                    });
                }
                this.render();
            } catch (error) {
                this._reportActionError(error);
            }
            return;
        }
        if (event.target?.matches?.("[data-operation-confidence-for]")) {
            this._applyLiveOperationMetadata(
                event.target.getAttribute?.("data-operation-confidence-for") ?? "",
            );
        }
    }

    _handleClick(event) {
        const automaticConditionTrigger = event.target?.closest?.(
            "button[data-rate-alternative-auto]",
        );
        if (
            automaticConditionTrigger
            && automaticConditionTrigger.getAttribute?.(
                "data-rate-alternative-auto",
            ) != null
        ) {
            event.preventDefault?.();
            try {
                const familyId = String(
                    automaticConditionTrigger.getAttribute?.(
                        "data-loot-condition-key",
                    ) ?? "",
                ).trim();
                if (!familyId) {
                    throw new TypeError(
                        "automatic rate alternative trigger has no family ID",
                    );
                }
                this.dispatchEvent(customEvent(
                    CALCULATOR_RATE_ALTERNATIVE_SELECTED_EVENT,
                    Object.freeze({
                        modelRevision: this._model?.modelRevision,
                        familyId,
                        alternativeId: null,
                    }),
                ));
            } catch (error) {
                this._reportActionError(error);
            }
            return;
        }
        const conditionTrigger = event.target?.closest?.(
            "button[data-loot-condition-option-id]",
        );
        if (
            conditionTrigger
            && conditionTrigger.getAttribute?.("data-loot-condition-option-id") != null
        ) {
            event.preventDefault?.();
            try {
                const selection = lootConditionSwitcherActionFromTrigger(conditionTrigger);
                this.dispatchEvent(customEvent(
                    CALCULATOR_RATE_ALTERNATIVE_SELECTED_EVENT,
                    Object.freeze({
                        modelRevision: this._model?.modelRevision,
                        familyId: selection.key,
                        alternativeId: selection.optionId,
                    }),
                ));
            } catch (error) {
                this._reportActionError(error);
            }
            return;
        }
        const trigger = event.target?.closest?.("[data-action]");
        if (!trigger) {
            return;
        }
        const action = trigger.getAttribute("data-action");
        try {
            if (action === "filter-ranking-evidence") {
                this._announcement = {
                    key: "calculator.data.announcement.filter_applied",
                    fallback: "Ranking evidence filter applied.",
                    variables: {},
                };
                this.render();
                return;
            }
            if (action === "select-data-item") {
                const itemId = this._selectItem(
                    trigger.getAttribute("data-item-id"),
                );
                this._requestItemModel(itemId);
                this.render();
                return;
            }
            if (action === "remove-custom-catalog-item") {
                this._removeCustomCatalogItem(
                    trigger.getAttribute("data-item-id"),
                );
                return;
            }
            if (action === "toggle-catalog-item-facts") {
                const itemId = parseCalculatorLootEditorItemId(
                    trigger.getAttribute("data-item-id"),
                );
                const opening = this._itemFactsEditorItemId !== itemId;
                this._itemFactsEditorItemId = opening ? itemId : null;
                if (opening) {
                    this._selectItem(itemId);
                    this._requestItemModel(itemId);
                }
                this._errorMessage = "";
                this.render();
                if (opening) {
                    this.querySelector?.(
                        `[data-catalog-item-facts-form][data-item-id="${itemId}"] [data-catalog-item-price]`,
                    )?.focus?.();
                }
                return;
            }
            if (action === "add-selected-item-to-group") {
                this._assignItemToGroup(
                    this._selectedItemId,
                    trigger.getAttribute("data-group-id") ?? "",
                );
                return;
            }
            if (action === "remove-item-from-group") {
                this._removeItemFromGroup(
                    Number(trigger.getAttribute("data-item-id")),
                    trigger.getAttribute("data-group-id") ?? "",
                );
                return;
            }
            if (action === "activate-loot-group" || action === "deactivate-loot-group") {
                this._setGroupPresence(
                    trigger.getAttribute("data-group-id") ?? "",
                    action === "activate-loot-group" ? "present" : "absent",
                );
                return;
            }
            if (action === "select-loot-group") {
                this._selectLootGroup(
                    trigger.getAttribute("data-group-id") ?? "",
                );
                this.render();
                return;
            }
            if (
                action === "edit-group-rate"
                || action === "edit-item-rate"
                || action === "create-group-rate-override"
                || action === "create-item-rate-override"
            ) {
                const scope = action === "edit-group-rate"
                    || action === "create-group-rate-override"
                    ? "group"
                    : "item";
                const itemId = scope === "item"
                    ? Number(trigger.getAttribute("data-item-id"))
                    : null;
                const nextRateEditor = Object.freeze({
                    scope,
                    groupId: trigger.getAttribute("data-group-id") ?? "",
                    itemId,
                    createOverride: action === "create-group-rate-override"
                        || action === "create-item-rate-override",
                });
                const sameRateEditor = this._rateEditor?.scope === nextRateEditor.scope
                    && this._rateEditor.groupId === nextRateEditor.groupId
                    && this._rateEditor.itemId === nextRateEditor.itemId
                    && Boolean(this._rateEditor.createOverride)
                        === nextRateEditor.createOverride;
                if (scope === "group") {
                    this._selectLootGroup(nextRateEditor.groupId);
                } else {
                    this._selectLootRow(
                        itemId,
                        nextRateEditor.groupId,
                        trigger.getAttribute("data-trace-id"),
                    );
                }
                this._rateEditor = sameRateEditor ? null : nextRateEditor;
                this._errorMessage = "";
                this.render();
                if (this._rateEditor) {
                    this.querySelector?.("[data-loot-editor-rate-form] [data-loot-editor-rate-value]")
                        ?.focus?.();
                }
                return;
            }
            if (action === "review-ranking-item" || action === "select-loot-row") {
                const itemId = Number(trigger.getAttribute("data-item-id"));
                const requestedTraceId = action === "select-loot-row"
                    ? trigger.getAttribute("data-trace-id")
                    : trigger.getAttribute("data-trace-id")
                        ?? this._projection?.provenanceTraces?.find((trace) => (
                            trace.factKey?.kind === "itemPresence"
                            && trace.factKey.itemId === itemId
                        ))?.traceId;
                if (action === "select-loot-row") {
                    this._selectLootRow(
                        itemId,
                        trigger.getAttribute("data-group-id") ?? "",
                        requestedTraceId,
                    );
                } else {
                    this._selectItem(itemId, requestedTraceId);
                }
                this.render();
                this.querySelector?.("[data-edit-membership-group]")?.focus?.();
                return;
            }
            let draft;
            if (action === "set-item-presence") {
                draft = buildSetItemPresenceDraft({
                    ...this._editIdentity(),
                    presence: this._value("[data-edit-presence]"),
                });
            } else if (action === "set-item-group-membership") {
                const identity = this._editIdentity();
                draft = buildSetItemGroupMembershipDraft({
                    fishingSource: identity.fishingSource,
                    itemId: identity.itemId,
                    groupId: this._value("[data-edit-membership-group]"),
                    membership: this._value("[data-edit-membership-state]"),
                });
            } else if (action === "refute-evidence") {
                const targetFactId = trigger.getAttribute("data-fact-id") ?? "";
                const card = trigger.closest?.("[data-provenance-fact-card]") ?? this;
                const reasonInput = this._elementWithAttribute(
                    "[data-refute-reason-for]",
                    "data-refute-reason-for",
                    targetFactId,
                    card,
                );
                draft = buildRefuteEvidenceDraft({
                    targetFactId,
                    reasonCode: String(reasonInput?.value ?? "").trim(),
                });
            } else if (action === "revert-user-operation") {
                draft = buildRevertUserOperationDraft(
                    trigger.getAttribute("data-operation-id") ?? "",
                );
            } else {
                return;
            }
            this.requestDraft(draft);
        } catch (error) {
            this._reportActionError(error);
        }
    }
}

export function registerCalculatorLayerInspector() {
    if (
        typeof globalThis.customElements !== "undefined"
        && !globalThis.customElements.get(CALCULATOR_LAYER_INSPECTOR_TAG)
    ) {
        globalThis.customElements.define(
            CALCULATOR_LAYER_INSPECTOR_TAG,
            FishyCalculatorLayerInspector,
        );
    }
}
